import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskResponsibilities, useEligibleUsers } from '../../hooks/useTaskResponsibilities';
import { useBulkUpdateResponsibilities } from '../../hooks/useResponsibilityMutations';
import { useWorkloadStatistics, useRedistributeFromUser, useRebalanceAllAffaires } from '../../hooks/useWorkloadStats';
import { UserResponsibilityList } from '../UserResponsibilityList';
import { ResponsibilityActionDialog, type ActionType } from '../ResponsibilityActionDialog';
import type { TaskType } from '../../types';
import './ResponsibilityManager.css';

interface UserResponsibilityState {
  userId: string;
  isResponsible: boolean;
  daysPerWeek: number; // 0.0-5.0
}

interface ResponsibilityManagerProps {
  onHasChangesChange?: (hasChanges: boolean) => void;
}

const ResponsibilityManager: React.FC<ResponsibilityManagerProps> = ({ onHasChangesChange }) => {
  const { t } = useTranslation();
  const [selectedTaskType, setSelectedTaskType] = useState<TaskType>('affaire_tracker');
  const [userResponsibilities, setUserResponsibilities] = useState<Map<string, UserResponsibilityState>>(new Map());

  // Dialog state
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    actionType: ActionType;
    userId: string;
    userName: string;
    affaireCount: number;
    pendingChange?: {
      type: 'toggle' | 'capacity';
      value: boolean | number;
    };
  }>({
    isOpen: false,
    actionType: 'remove',
    userId: '',
    userName: '',
    affaireCount: 0
  });

  // Determine which roles are eligible based on task type
  const getEligibleRoles = (taskType: TaskType): string[] => {
    switch (taskType) {
      case 'affaire_tracker':
        // Only admin and bureau for affaire tracker
        return ['admin', 'bureau'];
      case 'inspection':
      case 'reporting':
      default:
        // All eligible roles for other task types
        return ['admin', 'bureau', 'expert'];
    }
  };

  const eligibleRoles = getEligibleRoles(selectedTaskType);

  const { data: responsibilities, isLoading: loadingResponsibilities } = useTaskResponsibilities({
    taskType: selectedTaskType,
    isActive: true
  });

  const { data: eligibleUsers, isLoading: loadingUsers } = useEligibleUsers(eligibleRoles);
  const { data: workloadStats } = useWorkloadStatistics();
  const bulkUpdate = useBulkUpdateResponsibilities();
  const redistributeMutation = useRedistributeFromUser();
  const rebalanceMutation = useRebalanceAllAffaires();

  // Initialize user responsibilities state when data loads
  React.useEffect(() => {
    if (responsibilities && eligibleUsers) {
      const newMap = new Map<string, UserResponsibilityState>();

      eligibleUsers.forEach(user => {
        const existingResponsibility = responsibilities.find(r => r.userId === user.id);

        newMap.set(user.id, {
          userId: user.id,
          isResponsible: !!existingResponsibility,
          daysPerWeek: existingResponsibility?.daysPerWeek ?? 0.0
        });
      });

      setUserResponsibilities(newMap);
    }
  }, [responsibilities, eligibleUsers]);

  const handleToggleResponsibility = async (userId: string, isResponsible: boolean) => {
    const current = userResponsibilities.get(userId);
    if (!current) return;

    // If turning OFF (removing responsable), check they have no affaires
    if (current.isResponsible && !isResponsible && selectedTaskType === 'affaire_tracker') {
      const userWorkload = workloadStats?.responsables.find(r => r.id === userId);
      const affaireCount = userWorkload?.current_active_affaire_count || 0;

      if (affaireCount > 0) {
        // Cannot deactivate - must redistribute first
        // Step 1: Set capacity to 0
        // Step 2: Rebalance to redistribute all affaires
        // Step 3: Then deactivate
        try {
          // First, set days_per_week to 0
          const updated = new Map(userResponsibilities);
          updated.set(userId, {
            ...current,
            daysPerWeek: 0
          });

          const responsibilitiesToSave = Array.from(updated.values()).map(ur => ({
            userId: ur.userId,
            daysPerWeek: ur.daysPerWeek,
            isActive: ur.isResponsible
          }));

          await bulkUpdate.mutateAsync({
            taskType: selectedTaskType,
            responsibilities: responsibilitiesToSave
          });

          // Then trigger full rebalance - this will move all affaires away from user with 0 capacity
          await rebalanceMutation.mutateAsync({
            notes: `User being deactivated - capacity set to 0 and affaires redistributed`
          });

          // Now deactivate the user
          const finalUpdated = new Map(userResponsibilities);
          finalUpdated.set(userId, {
            ...current,
            daysPerWeek: 0,
            isResponsible: false
          });

          const finalResponsibilities = Array.from(finalUpdated.values()).map(ur => ({
            userId: ur.userId,
            daysPerWeek: ur.daysPerWeek,
            isActive: ur.isResponsible
          }));

          await bulkUpdate.mutateAsync({
            taskType: selectedTaskType,
            responsibilities: finalResponsibilities
          });

          // Queries will refetch automatically
          return;
        } catch (error) {
          console.error('Failed to redistribute and deactivate:', error);
          return;
        }
      }
    }

    // No affaires or turning ON - save change immediately
    try {
      const updated = new Map(userResponsibilities);
      updated.set(userId, {
        ...current,
        isResponsible
      });

      const responsibilitiesToSave = Array.from(updated.values()).map(ur => ({
        userId: ur.userId,
        daysPerWeek: ur.daysPerWeek,
        isActive: ur.isResponsible
      }));

      await bulkUpdate.mutateAsync({
        taskType: selectedTaskType,
        responsibilities: responsibilitiesToSave
      });

      setUserResponsibilities(updated);
    } catch (error) {
      console.error('Failed to toggle responsibility:', error);
    }
  };

  const handleAvailabilityChange = async (
    userId: string,
    daysPerWeek: number
  ) => {
    const current = userResponsibilities.get(userId);
    if (!current) return;

    // If changing capacity for an active responsable
    if (current.isResponsible && selectedTaskType === 'affaire_tracker') {
      // Show dialog if the capacity is changing significantly
      if (Math.abs(current.daysPerWeek - daysPerWeek) >= 0.5) {
        const user = eligibleUsers?.find(u => u.id === userId);
        const userWorkload = workloadStats?.responsables.find(r => r.id === userId);
        const affaireCount = userWorkload?.current_active_affaire_count || 0;

        setDialogState({
          isOpen: true,
          actionType: 'capacity-change',
          userId,
          userName: user ? `${user.firstName} ${user.lastName}` : '',
          affaireCount,
          pendingChange: { type: 'capacity', value: daysPerWeek }
        });
        return;
      }
    }

    // No dialog needed, save change immediately
    try {
      const updated = new Map(userResponsibilities);
      updated.set(userId, {
        ...current,
        daysPerWeek
      });

      const responsibilitiesToSave = Array.from(updated.values()).map(ur => ({
        userId: ur.userId,
        daysPerWeek: ur.daysPerWeek,
        isActive: ur.isResponsible
      }));

      await bulkUpdate.mutateAsync({
        taskType: selectedTaskType,
        responsibilities: responsibilitiesToSave
      });

      setUserResponsibilities(updated);
    } catch (error) {
      console.error('Failed to update availability:', error);
    }
  };


  // Dialog action: Keep assignments (or apply only to future)
  const handleDialogKeep = () => {
    const { userId, pendingChange } = dialogState;
    if (!pendingChange) return;

    const current = userResponsibilities.get(userId);
    if (!current) return;

    // Apply the change without redistributing/rebalancing
    const updated = new Map(userResponsibilities);
    if (pendingChange.type === 'toggle') {
      updated.set(userId, {
        ...current,
        isResponsible: pendingChange.value as boolean
      });
    } else if (pendingChange.type === 'capacity') {
      updated.set(userId, {
        ...current,
        daysPerWeek: pendingChange.value as number
      });
    }

    setUserResponsibilities(updated);
    setDialogState({ ...dialogState, isOpen: false });
  };

  // Dialog action: Redistribute/Rebalance
  const handleDialogRedistribute = async () => {
    const { userId, actionType, pendingChange } = dialogState;
    if (!pendingChange) return;

    try {
      if (actionType === 'remove') {
        // Redistribute this user's affaires to others AND deactivate them
        // The backend handles both operations atomically
        await redistributeMutation.mutateAsync({
          userId,
          notes: 'User removed from responsables - affaires redistributed'
        });

        // No need to update local state - queries will refetch automatically
        // and show the updated state from the server

      } else if (actionType === 'capacity-change') {
        // First apply the capacity change
        const current = userResponsibilities.get(userId);
        if (current) {
          const updated = new Map(userResponsibilities);
          updated.set(userId, {
            ...current,
            daysPerWeek: pendingChange.value as number
          });
          setUserResponsibilities(updated);

          // Save the change immediately
          const responsibilitiesToSave = Array.from(updated.values()).map(ur => ({
            userId: ur.userId,
            daysPerWeek: ur.daysPerWeek,
            isActive: ur.isResponsible
          }));

          await bulkUpdate.mutateAsync({
            taskType: selectedTaskType,
            responsibilities: responsibilitiesToSave
          });

          // Then trigger rebalancing
          await rebalanceMutation.mutateAsync({
            notes: 'Capacity updated - full rebalancing triggered'
          });
        }
      }

      setDialogState({ ...dialogState, isOpen: false });

    } catch (error) {
      console.error('Failed to redistribute/rebalance:', error);
      // Keep dialog open to show error
    }
  };

  const loading = loadingResponsibilities || loadingUsers;
  const isProcessing = redistributeMutation.isPending || rebalanceMutation.isPending;

  return (
    <div className="responsibility-manager">
      {/* Task Type Display */}
      <div className="responsibility-manager-selector">
        <div className="responsibility-manager-selector-left">
          <label className="responsibility-manager-label">
            {t('admin.userManagement.responsibilities.taskType', 'Type de tâche')}
          </label>
          <div className="responsibility-manager-task-type-display">
            {t('admin.userManagement.responsibilities.taskTypes.affaireTracker', 'Responsable Affaire (Tracker)')}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="responsibility-manager-description">
        <p>
          {t(
            'admin.userManagement.responsibilities.description',
            'Sélectionnez les utilisateurs qui peuvent être responsables de ce type de tâche et définissez leur disponibilité hebdomadaire.'
          )}
        </p>
      </div>

      {/* User Responsibility List */}
      {loading ? (
        <div className="responsibility-manager-loading">
          {t('common.loading', 'Chargement...')}
        </div>
      ) : (
        <UserResponsibilityList
          users={eligibleUsers || []}
          userResponsibilities={userResponsibilities}
          onToggleResponsibility={handleToggleResponsibility}
          onAvailabilityChange={handleAvailabilityChange}
        />
      )}

      {/* Confirmation Dialog */}
      <ResponsibilityActionDialog
        isOpen={dialogState.isOpen}
        actionType={dialogState.actionType}
        userName={dialogState.userName}
        affaireCount={dialogState.affaireCount}
        onClose={() => setDialogState({ ...dialogState, isOpen: false })}
        onKeep={handleDialogKeep}
        onRedistribute={handleDialogRedistribute}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export { ResponsibilityManager };
export default ResponsibilityManager;
